import React, { useEffect, useState } from 'react'
import {
	Table,
	TableRow,
	TableCell,
	TableBody,
	TableHead,
	Checkbox,
	Button,
} from '@material-ui/core'
import { red, green, yellow } from '@material-ui/core/colors'
import { requestAPI } from '../../data/api'
import Loading from '../../generic/Loading'
import { getApiPath } from '../../data/api'
import { getModel } from '../../data/Model'
import axios from 'axios'
import ValidateCasesDialog from './ValidateCasesDialog'

export default function CasesOverview() {
	const [clients, setClients] = useState([])
	const [loading, setLoading] = useState(false)
	const [selectedClients, setSelectedClients] = useState([])
	const [exporting, setExporting] = useState(false)
	const [validationDialogOpen, setValidationDialogOpen] = useState(false)
	const [validationCases, setValidationCases] = useState([])
	const [validationLoading, setValidationLoading] = useState(false)
	const [validationError, setValidationError] = useState(null)
	const [selectedClientForValidation, setSelectedClientForValidation] =
		useState(null)

	async function fetchClients() {
		setLoading(true)
		const response = await requestAPI(`/cases/export/`, 'GET')
		setClients(response)
		setLoading(false)
	}

	useEffect(() => {
		fetchClients()
	}, [])

	const handleSelectClient = (clientId) => {
		setSelectedClients((prev) => {
			if (prev.includes(clientId)) {
				return prev.filter((id) => id !== clientId)
			} else {
				return [...prev, clientId]
			}
		})
	}

	const handleSelectAll = () => {
		if (selectedClients.length === clients.length) {
			setSelectedClients([])
		} else {
			setSelectedClients(clients.map((c) => c.client_id))
		}
	}

	const handleOpenValidation = async (clientId, clientName) => {
		setSelectedClientForValidation({ clientId, clientName })
		setValidationDialogOpen(true)
		setValidationLoading(true)
		setValidationError(null)
		setValidationCases([])

		try {
			const url = getApiPath(
				`/cases/export/validate-exports?client_id=${clientId}&auth_token=${getModel().auth_token}`,
			)
			const response = await axios.get(url)
			setValidationCases(response.data.cases || [])
		} catch (error) {
			console.error('Failed to fetch validation cases:', error)
			setValidationError('Failed to load cases. Please try again.')
		} finally {
			setValidationLoading(false)
		}
	}

	const refetchValidationCases = async () => {
		if (selectedClientForValidation) {
			setValidationLoading(true)
			setValidationError(null)

			try {
				const url = getApiPath(
					`/cases/export/validate-exports?client_id=${selectedClientForValidation.clientId}&auth_token=${getModel().auth_token}`,
				)
				const response = await axios.get(url)
				setValidationCases(response.data.cases || [])
			} catch (error) {
				console.error('Failed to fetch validation cases:', error)
				setValidationError('Failed to load cases. Please try again.')
			} finally {
				setValidationLoading(false)
			}
		}
	}

	const handleCloseValidation = () => {
		setValidationDialogOpen(false)
		setValidationCases([])
		setValidationError(null)
		setSelectedClientForValidation(null)
	}

	const handleBulkExport = async () => {
		if (selectedClients.length === 0) {
			window.alert('Please select at least one client to export')
			return
		}

		setExporting(true)

		// Check exportability for all selected clients
		const exportabilityChecks = await Promise.all(
			selectedClients.map(async (clientId) => {
				try {
					const checkUrl = getApiPath(
						`/cases/export/validate-partner?client_id=${clientId}&auth_token=${getModel().auth_token}`,
					)
					const response = await axios({ url: checkUrl, method: 'GET' })
					const client = clients.find((c) => c.client_id === clientId)
					return {
						clientId,
						exportable: response.data.exportable,
						clientName: client ? client.name : clientId,
						missingFields: response.data.missingFields || [],
					}
				} catch (error) {
					console.error(
						`Failed to check exportability for client ${clientId}:`,
						error,
					)
					const client = clients.find((c) => c.client_id === clientId)
					return {
						clientId,
						exportable: false,
						clientName: client ? client.name : clientId,
						missingFields: [],
					}
				}
			}),
		)

		// Filter out non-exportable clients
		const nonExportable = exportabilityChecks.filter(
			(check) => !check.exportable,
		)
		const exportableClientIds = exportabilityChecks
			.filter((check) => check.exportable)
			.map((check) => check.clientId)

		// Report non-exportable clients
		if (nonExportable.length > 0) {
			const details = nonExportable
				.map((check) => {
					const missingFieldsText =
						check.missingFields && check.missingFields.length > 0
							? `Missing: ${check.missingFields.join(', ')}`
							: 'Missing required fields'
					return `${check.clientName}: ${missingFieldsText}`
				})
				.join('\n')
			window.alert(
				`The following clients cannot be exported:\n\n${details}\n\nPlease fill in the required fields in settings and try again.`,
			)
			setExporting(false)
			return
		}

		// If no clients are exportable, stop here
		if (exportableClientIds.length === 0) {
			setExporting(false)
			return
		}

		// Step 1: Calculate pricing for all exportable clients
		const pricingErrors = []
		for (const clientId of exportableClientIds) {
			const calculatePricingUrl = getApiPath(
				`/cases/export/calculate-pricing?client_id=${clientId}&auth_token=${getModel().auth_token}`,
			)
			try {
				const pricingResponse = await axios.post(calculatePricingUrl)
				if (
					!pricingResponse.data.success &&
					pricingResponse.data.errors &&
					pricingResponse.data.errors.length > 0
				) {
					const client = clients.find((c) => c.client_id === clientId)
					const clientName = client ? client.name : clientId
					const errorDetails = pricingResponse.data.errors
						.map((err) => `  ${err.caseId || err.violationId}: ${err.error}`)
						.join('\n')
					pricingErrors.push(`${clientName}:\n${errorDetails}`)
				}
			} catch (error) {
				console.error(
					`Failed to calculate pricing for client ${clientId}:`,
					error,
				)
				const client = clients.find((c) => c.client_id === clientId)
				const clientName = client ? client.name : clientId
				pricingErrors.push(`${clientName}: Failed to calculate pricing`)
			}
		}

		// Report pricing errors if any
		if (pricingErrors.length > 0) {
			window.alert(
				`Some clients failed pricing calculation:\n\n${pricingErrors.join('\n\n')}`,
			)
			setExporting(false)
			return
		}

		// Step 2: Export all exportable clients using the new export endpoint
		try {
			const exportErrors = []
			for (const clientId of exportableClientIds) {
				const url = getApiPath(
					`/cases/export?client_id=${clientId}&auth_token=${getModel().auth_token}`,
				)
				try {
					await axios.post(url)
				} catch (error) {
					console.error(`Failed to export client ${clientId}:`, error)
					if (
						error.response &&
						error.response.status === 400 &&
						error.response.data
					) {
						const { invalidViolations } = error.response.data
						const client = clients.find((c) => c.client_id === clientId)
						const clientName = client ? client.name : clientId
						if (invalidViolations && invalidViolations.length > 0) {
							const details = invalidViolations
								.map((v) => `${v.caseId}: ${v.missingFields.join(', ')}`)
								.join('\n')
							exportErrors.push(`${clientName}:\n${details}`)
						} else {
							exportErrors.push(`${clientName}: Unknown error`)
						}
					} else {
						const client = clients.find((c) => c.client_id === clientId)
						const clientName = client ? client.name : clientId
						exportErrors.push(`${clientName}: Failed to export`)
					}
				}
			}
			await fetchClients()

			if (exportErrors.length > 0) {
				window.alert(
					`Some clients could not be exported due to missing fields:\n\n${exportErrors.join('\n\n')}\n\nPlease fill in the missing fields and try again.`,
				)
				setExporting(false)
				return
			}
		} catch (error) {
			console.error('Failed to export clients:', error)
			window.alert('Failed to export clients. Please try again.')
		}

		setExporting(false)
		setSelectedClients([])
	}

	return (
		<div className='container'>
			<h1 style={{ marginTop: '30px', marginBottom: '20px' }}>
				Cases ready for export
			</h1>
			{selectedClients.length > 0 && (
				<div style={{ marginBottom: '20px' }}>
					<Button
						variant='contained'
						color='primary'
						onClick={handleBulkExport}
						disabled={exporting}
					>
						{exporting
							? 'Exporting...'
							: `Export ${selectedClients.length} Selected Client${selectedClients.length > 1 ? 's' : ''}`}
					</Button>
				</div>
			)}
			<div style={{ marginBottom: '20px' }}>
				{renderBody(
					loading,
					clients,
					selectedClients,
					handleSelectClient,
					handleSelectAll,
					handleOpenValidation,
					fetchClients,
				)}
			</div>
			<ValidateCasesDialog
				open={validationDialogOpen}
				onClose={handleCloseValidation}
				cases={validationCases}
				loading={validationLoading}
				error={validationError}
				clientName={
					(selectedClientForValidation &&
						selectedClientForValidation.clientName) ||
					''
				}
				refetchCases={refetchValidationCases}
			/>
		</div>
	)
}

function renderBody(
	loading,
	clients,
	selectedClients,
	handleSelectClient,
	handleSelectAll,
	handleOpenValidation,
	fetchClients,
) {
	if (loading) return renderLoading()
	if (clients.length === 0) {
		return (
			<div>
				<h3>No clients</h3>
			</div>
		)
	}
	return renderTable(
		clients,
		selectedClients,
		handleSelectClient,
		handleSelectAll,
		handleOpenValidation,
		fetchClients,
	)
}

function renderLoading() {
	return (
		<div style={{ maxWidth: 100 }}>
			<Loading />
		</div>
	)
}

function getColor(number) {
	if (number <= 0) return red[500]
	if (number <= 9) return yellow[500]
	return green[500]
}

async function exportCases(clientId, fetchClients) {
	const checkExportabilityUrl = getApiPath(
		`/cases/export/validate-partner?client_id=${clientId}&auth_token=${getModel().auth_token}`,
	)

	const checkExportabilityResponse = await axios({
		url: checkExportabilityUrl,
		method: 'GET',
	})

	if (!checkExportabilityResponse.data.exportable) {
		const missingFields = checkExportabilityResponse.data.missingFields || []
		const message =
			missingFields.length > 0
				? `Missing required fields: ${missingFields.join(', ')}`
				: 'Missing required fields in settings'
		window.alert(
			`Cannot export: ${message}\n\nPlease fill in the required fields in settings and try again.`,
		)
		return
	}

	// Step 1: Calculate pricing for all cases
	const calculatePricingUrl = getApiPath(
		`/cases/export/calculate-pricing?client_id=${clientId}&auth_token=${getModel().auth_token}`,
	)
	try {
		const pricingResponse = await axios.post(calculatePricingUrl)

		// Check if there were any pricing errors
		if (
			!pricingResponse.data.success &&
			pricingResponse.data.errors &&
			pricingResponse.data.errors.length > 0
		) {
			const errorDetails = pricingResponse.data.errors
				.map((err) => `${err.caseId || err.violationId}: ${err.error}`)
				.join('\n')
			window.alert(
				`Error: ${pricingResponse.data.errors.length} case(s) failed pricing calculation:\n\n${errorDetails}\n\nDo you want to continue with export for the remaining cases?`,
			)
			return
		}
	} catch (error) {
		console.error(error.response.data.errors)
		console.error('Failed to calculate pricing:', error)
		window.alert(
			'Failed to calculate pricing. \n\n' +
				error.response.data.errors
					.map((error) => `Case ID: ${error.caseId} error: ${error.error}`)
					.join('\n\n'),
		)
		return
	}

	// Step 2: Export cases
	const url = getApiPath(
		`/cases/export?client_id=${clientId}&auth_token=${getModel().auth_token}`,
	)
	try {
		await axios.post(url)
		await fetchClients()
	} catch (error) {
		console.error('Failed to export client:', error)
		if (
			error.response &&
			error.response.status === 400 &&
			error.response.data
		) {
			const { invalidViolations } = error.response.data
			if (invalidViolations && invalidViolations.length > 0) {
				const details = invalidViolations
					.map((v) => `${v.caseId}: ${v.missingFields.join(', ')}`)
					.join('\n')
				window.alert(
					`Cannot export: Some cases are missing required fields\n\n${details}\n\nPlease fill in the missing fields and try again.`,
				)
			} else {
				window.alert('Failed to export client. Please try again.')
			}
		} else {
			window.alert('Failed to export client. Please try again.')
		}
	}
}

function renderTable(
	clients,
	selectedClients,
	handleSelectClient,
	handleSelectAll,
	handleOpenValidation,
	fetchClients,
) {
	const allSelected =
		selectedClients.length === clients.length && clients.length > 0

	return (
		<Table>
			<TableHead>
				<TableRow>
					<TableCell padding='checkbox'>
						<Checkbox
							checked={allSelected}
							indeterminate={
								selectedClients.length > 0 &&
								selectedClients.length < clients.length
							}
							onChange={handleSelectAll}
							color='primary'
						/>
					</TableCell>
					<TableCell>Client name</TableCell>
					<TableCell>Number of cases</TableCell>
					<TableCell>Validate</TableCell>
					<TableCell>Export</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{clients.map((client) => (
					<TableRow key={client.name}>
						<TableCell padding='checkbox'>
							<Checkbox
								checked={selectedClients.includes(client.client_id)}
								onChange={() => handleSelectClient(client.client_id)}
								color='primary'
							/>
						</TableCell>
						<TableCell>{client.name}</TableCell>
						<TableCell
							style={{
								color: getColor(client.cases),
								fontWeight: 600,
								fontSize: '1.1em',
							}}
						>
							{client.cases}
						</TableCell>
						<TableCell>
							<a
								className='btn'
								onClick={() =>
									handleOpenValidation(client.client_id, client.name)
								}
								style={{ cursor: 'pointer' }}
							>
								Validate
							</a>
						</TableCell>
						<TableCell>
							<a
								className='btn'
								onClick={() => exportCases(client.client_id, fetchClients)}
							>
								Export
							</a>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}
